import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAccessToken } from '../lib/auth-api';

const formeOptions = ['EI', 'EURL', 'SASU', 'SAS', 'SARL', 'SA', 'SNC', 'Autre'];
const regimeSocialOptions = [
  { value: '', label: 'Non renseigné' },
  { value: 'Micro-entrepreneur (AE)', label: 'Micro-entrepreneur (AE)' },
  { value: 'TNS', label: 'TNS' },
  { value: 'Salarié', label: 'Salarié' },
  { value: 'Gérant assimilé salarié', label: 'Gérant assimilé salarié' },
];

const inputClass = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent';
const labelClass = 'block text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1';

interface PersonalForm {
  prenom: string;
  nom: string;
  telephone: string;
  rue: string;
  ville: string;
  code_postal: string;
  pays: string;
  regime_social: string;
  occupation: string;
}

interface CompanyForm {
  raison_sociale: string;
  siret: string;
  code_ape: string;
  forme_juridique: string;
  regime_fiscal: string;
  regime_tva: string;
  banque: string;
  iban: string;
  date_cloture_exercice: string;
}

interface Entreprise {
  id: string;
  raison_sociale: string;
  forme_juridique?: string;
  siret?: string;
  code_ape?: string;
  regime_fiscal?: string;
  regime_tva?: string;
  date_cloture_exercice?: string;
  deleted_at?: string | null;
  exercices?: { id: string; debut: string; fin: string; is_current: boolean }[];
}

export default function ProfilePage() {
  const { user } = useAuth();
  const entrepriseId = localStorage.getItem('entreprise_id');

  const [profileExists, setProfileExists] = useState(false);
  const [personal, setPersonal] = useState<PersonalForm>({
    prenom: '', nom: '', telephone: '', rue: '', ville: '',
    code_postal: '', pays: 'FR', regime_social: '', occupation: '',
  });
  const [company, setCompany] = useState<CompanyForm>({
    raison_sociale: '', siret: '', code_ape: '', forme_juridique: 'EI',
    regime_fiscal: 'IS', regime_tva: 'Simplifie', banque: '', iban: '',
    date_cloture_exercice: '12-31',
  });
  const [companies, setCompanies] = useState<Entreprise[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingPersonal, setSavingPersonal] = useState(false);
  const [savingCompany, setSavingCompany] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [editCompany, setEditCompany] = useState<Entreprise | null>(null);
  const [editForm, setEditForm] = useState<CompanyForm>({ raison_sociale: '', siret: '', code_ape: '', forme_juridique: 'EI', regime_fiscal: 'IS', regime_tva: 'Simplifie', banque: '', iban: '', date_cloture_exercice: '12-31' });
  const [savingEdit, setSavingEdit] = useState(false);
  const openEditCompany = (c: Entreprise) => {
    setEditForm({
      raison_sociale: c.raison_sociale || '',
      siret: c.siret || '',
      code_ape: c.code_ape || '',
      forme_juridique: c.forme_juridique || 'EI',
      regime_fiscal: c.regime_fiscal || 'IS',
      regime_tva: c.regime_tva || 'Simplifie',
      banque: '',
      iban: '',
      date_cloture_exercice: c.date_cloture_exercice || '12-31',
    });
    setEditCompany(c);
  };

  const handleSaveEdit = async () => {
    if (!editCompany) return;
    setSavingEdit(true);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${apiUrl}/api/entreprise/${editCompany.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          raison_sociale: editForm.raison_sociale,
          siret: editForm.siret || undefined,
          code_ape: editForm.code_ape || undefined,
          forme_juridique: editForm.forme_juridique,
          regime_fiscal: editForm.regime_fiscal,
          regime_tva: editForm.regime_tva,
          date_cloture_exercice: editForm.date_cloture_exercice,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || `HTTP ${res.status}`); }
      const updated = await res.json();
      setCompanies((prev) => prev.map((c) => c.id === updated.id ? { ...c, ...updated } : c));
      setEditCompany(null);
      showToast('Société mise à jour ✓');
    } catch (e: any) {
      setError(`Erreur: ${e.message}`);
    } finally {
      setSavingEdit(false);
    }
  };

  const apiUrl = import.meta.env.VITE_API_URL || '';

  const getHeaders = () => {
    const token = getAccessToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = await getHeaders();

        // Fetch profile
        const profileRes = await fetch(`${apiUrl}/api/user/profile`, { headers });
        if (profileRes.ok) {
          const p = await profileRes.json();
          setPersonal({
            prenom: p.prenom || '', nom: p.nom || '', telephone: p.telephone || '',
            rue: p.rue || '', ville: p.ville || '', code_postal: p.code_postal || '',
            pays: p.pays || 'FR', regime_social: p.regime_social || '', occupation: p.occupation || '',
          });
          setProfileExists(true);
        }

        // Fetch company
        if (entrepriseId) {
          const compRes = await fetch(`${apiUrl}/api/entreprise/me?entreprise_id=${entrepriseId}`, { headers });
          if (compRes.ok) {
            const c = await compRes.json();
            setCompany({
              raison_sociale: c.raison_sociale || '', siret: c.siret || '',
              code_ape: c.code_ape || '', forme_juridique: c.forme_juridique || 'EI',
              regime_fiscal: c.regime_fiscal || 'IS', regime_tva: c.regime_tva || 'Simplifie',
              banque: '', iban: '', date_cloture_exercice: c.date_cloture_exercice || '12-31',
            });
          }
        }

        // Fetch companies list
        const listRes = await fetch(`${apiUrl}/api/entreprises`, { headers });
        if (listRes.ok) {
          setCompanies(await listRes.json());
        }
      } catch {
        setError('Impossible de charger les données');
      } finally {
        setLoading(false);
      }
    })();
  }, [entrepriseId]);

  const handleSavePersonal = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingPersonal(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const method = profileExists ? 'PUT' : 'POST';
      const res = await fetch(`${apiUrl}/api/user/profile`, {
        method,
        headers,
        body: JSON.stringify(personal),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      setProfileExists(true);
      showToast('Profil mis à jour \u2713');
    } catch (e: any) {
      setError(`Erreur lors de la sauvegarde du profil: ${e.message}`);
    } finally {
      setSavingPersonal(false);
    }
  };

  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingCompany(true);
    setError(null);
    try {
      const headers = await getHeaders();
      const res = await fetch(`${apiUrl}/api/entreprise/${entrepriseId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          raison_sociale: company.raison_sociale,
          siret: company.siret || undefined,
          code_ape: company.code_ape || undefined,
          forme_juridique: company.forme_juridique,
          regime_fiscal: company.regime_fiscal,
          regime_tva: company.regime_tva,
          date_cloture_exercice: company.date_cloture_exercice,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      showToast('Société mise à jour \u2713');
    } catch (e: any) {
      setError(`Erreur lors de la sauvegarde de la société: ${e.message}`);
    } finally {
      setSavingCompany(false);
    }
  };

  const handleDeleteCompany = async (id: string) => {
    try {
      const headers = await getHeaders();
      // Remove Content-Type for DELETE (no body)
      const deleteHeaders = { ...headers };
      delete (deleteHeaders as any)['Content-Type'];
      const res = await fetch(`${apiUrl}/api/entreprise/${id}`, { method: 'DELETE', headers: deleteHeaders });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        let msg = `HTTP ${res.status}`;
        try { msg = JSON.parse(text).error || msg; } catch {}
        throw new Error(msg);
      }
      setCompanies(companies.filter((c) => c.id !== id));
      setDeleteConfirm(null);
      showToast('Société archivée');
    } catch (e: any) {
      setError(`Erreur lors de la suppression: ${e.message}`);
    }
  };

  const updatePersonal = (patch: Partial<PersonalForm>) => setPersonal({ ...personal, ...patch });
  const updateCompany = (patch: Partial<CompanyForm>) => setCompany({ ...company, ...patch });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <p className="text-[10px] uppercase tracking-wider text-amber-600 font-semibold mb-1">Paramètres</p>
        <h1 className="text-2xl font-bold text-gray-900">Mon Compte</h1>
        <p className="text-sm text-gray-500">{user?.email}</p>
      </div>

      {error && (
        <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">{error}</div>
      )}
      {toast && (
        <div className="text-green-700 bg-green-50 border border-green-200 px-4 py-3 rounded-lg text-sm mb-4">{toast}</div>
      )}

      {/* 2-column grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Card 1: Personal */}
        <form onSubmit={handleSavePersonal} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations personnelles</h2>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="prenom" className={labelClass}>Prénom</label>
                <input id="prenom" value={personal.prenom} onChange={(e) => updatePersonal({ prenom: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="nom" className={labelClass}>Nom</label>
                <input id="nom" value={personal.nom} onChange={(e) => updatePersonal({ nom: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div>
              <label className={labelClass}>Email</label>
              <input type="email" value={user?.email || ''} readOnly className={`${inputClass} bg-gray-50 text-gray-400 cursor-not-allowed`} />
            </div>

            <div>
              <label htmlFor="telephone" className={labelClass}>Téléphone</label>
              <input id="telephone" value={personal.telephone} onChange={(e) => updatePersonal({ telephone: e.target.value })} className={inputClass} />
            </div>

            <div>
              <label htmlFor="p_rue" className={labelClass}>Rue</label>
              <input id="p_rue" value={personal.rue} onChange={(e) => updatePersonal({ rue: e.target.value })} className={inputClass} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label htmlFor="p_cp" className={labelClass}>Code postal</label>
                <input id="p_cp" value={personal.code_postal} onChange={(e) => updatePersonal({ code_postal: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="p_ville" className={labelClass}>Ville</label>
                <input id="p_ville" value={personal.ville} onChange={(e) => updatePersonal({ ville: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="p_pays" className={labelClass}>Pays</label>
                <input id="p_pays" value={personal.pays} onChange={(e) => updatePersonal({ pays: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div>
              <label htmlFor="regime_social" className={labelClass}>Régime social</label>
              <select id="regime_social" value={personal.regime_social} onChange={(e) => updatePersonal({ regime_social: e.target.value })} className={inputClass}>
                {regimeSocialOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="occupation" className={labelClass}>Occupation</label>
              <input id="occupation" value={personal.occupation} onChange={(e) => updatePersonal({ occupation: e.target.value })} className={inputClass} />
            </div>
          </div>

          <button type="submit" disabled={savingPersonal} className="w-full mt-5 py-2.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors">
            {savingPersonal ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </form>

        {/* Card 2: Company */}
        <form onSubmit={handleSaveCompany} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Informations société</h2>

          <div className="space-y-3">
            <div>
              <label htmlFor="raison_sociale" className={labelClass}>Raison sociale</label>
              <input id="raison_sociale" value={company.raison_sociale} onChange={(e) => updateCompany({ raison_sociale: e.target.value })} className={inputClass} />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="siret" className={labelClass}>SIRET</label>
                <input id="siret" value={company.siret} onChange={(e) => updateCompany({ siret: e.target.value })} maxLength={14} className={inputClass} />
              </div>
              <div>
                <label htmlFor="code_ape" className={labelClass}>Code APE</label>
                <input id="code_ape" value={company.code_ape} onChange={(e) => updateCompany({ code_ape: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div>
              <label htmlFor="forme_juridique" className={labelClass}>Forme juridique</label>
              <select id="forme_juridique" value={company.forme_juridique} onChange={(e) => updateCompany({ forme_juridique: e.target.value })} className={inputClass}>
                {formeOptions.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="regime_fiscal" className={labelClass}>Régime fiscal</label>
                <select id="regime_fiscal" value={company.regime_fiscal} onChange={(e) => updateCompany({ regime_fiscal: e.target.value })} className={inputClass}>
                  <option value="IS">IS</option>
                  <option value="BIC">IR</option>
                </select>
              </div>
              <div>
                <label htmlFor="regime_tva" className={labelClass}>Régime TVA</label>
                <select
                  id="regime_tva"
                  value={company.regime_tva}
                  onChange={(e) => updateCompany({ regime_tva: e.target.value })}
                  className={inputClass}
                  title="Simplifié: CA < 254 000€ (services) ou 840 000€ (ventes) | Réel normal: au-delà des seuils | Franchise en base: exonéré de TVA (CA < 36 800€)"
                >
                  <option value="Simplifie">Simplifié</option>
                  <option value="ReelNormal">Réel normal</option>
                  <option value="Franchise">Franchise en base</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="banque" className={labelClass}>Banque</label>
                <input id="banque" value={company.banque} onChange={(e) => updateCompany({ banque: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label htmlFor="iban" className={labelClass}>IBAN</label>
                <input id="iban" value={company.iban} onChange={(e) => updateCompany({ iban: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div>
              <label htmlFor="date_cloture" className={labelClass}>Date de clôture</label>
              <input id="date_cloture" value={company.date_cloture_exercice} onChange={(e) => updateCompany({ date_cloture_exercice: e.target.value })} placeholder="MM-DD" className={inputClass} />
            </div>
          </div>

          <button type="submit" disabled={savingCompany} className="w-full mt-5 py-2.5 bg-amber-500 text-white text-sm font-medium rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors">
            {savingCompany ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </form>
      </div>

      {/* Mes sociétés */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Mes sociétés</h2>

        {companies.length === 0 ? (
          <p className="text-sm text-gray-500">Aucune société enregistrée.</p>
        ) : (
          <div className="space-y-2">
            {companies.map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3 px-4 rounded-lg border border-gray-100 hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray-900">{c.raison_sociale}</span>
                  {c.forme_juridique && (
                    <span className="text-xs text-gray-400">{c.forme_juridique}</span>
                  )}
                  {c.id === entrepriseId && (
                    <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold">Active</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {c.id !== entrepriseId && (
                    <button
                      onClick={() => {
                        localStorage.setItem('entreprise_id', c.id);
                        if (c.exercices?.[0]?.id) localStorage.setItem('exercice_id', c.exercices[0].id);
                        window.location.reload();
                      }}
                      className="text-xs text-amber-600 hover:text-amber-700 font-medium"
                    >
                      Sélectionner
                    </button>
                  )}
                  <button
                    onClick={() => openEditCompany(c)}
                    className="text-xs text-blue-500 hover:text-blue-700 font-medium"
                  >
                    Modifier
                  </button>
                  {companies.filter(co => !co.deleted_at).length > 1 && (
                    <button
                      onClick={() => setDeleteConfirm(c.id)}
                      className="text-xs text-red-400 hover:text-red-600"
                    >
                      Supprimer
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete confirm modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmer la suppression</h3>
            <p className="text-sm text-gray-600 mb-4">
              Cette action est irréversible. Toutes les données de {companies.find((c) => c.id === deleteConfirm)?.raison_sociale} seront archivées.
            </p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button onClick={() => handleDeleteCompany(deleteConfirm)} className="flex-1 py-2 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {editCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Modifier — {editCompany.raison_sociale}</h3>
            <div className="space-y-3">
              <div>
                <label className={labelClass}>Raison sociale</label>
                <input value={editForm.raison_sociale} onChange={(e) => setEditForm((f) => ({ ...f, raison_sociale: e.target.value }))} className={inputClass} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>SIRET</label>
                  <input value={editForm.siret} onChange={(e) => setEditForm((f) => ({ ...f, siret: e.target.value }))} maxLength={14} className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Code APE</label>
                  <input value={editForm.code_ape} onChange={(e) => setEditForm((f) => ({ ...f, code_ape: e.target.value }))} className={inputClass} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Forme juridique</label>
                <select value={editForm.forme_juridique} onChange={(e) => setEditForm((f) => ({ ...f, forme_juridique: e.target.value }))} className={inputClass}>
                  {formeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Régime fiscal</label>
                  <select value={editForm.regime_fiscal} onChange={(e) => setEditForm((f) => ({ ...f, regime_fiscal: e.target.value }))} className={inputClass}>
                    <option value="IS">IS</option>
                    <option value="BIC">IR</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Régime TVA</label>
                  <select value={editForm.regime_tva} onChange={(e) => setEditForm((f) => ({ ...f, regime_tva: e.target.value }))} className={inputClass}>
                    <option value="Simplifie">Simplifié</option>
                    <option value="ReelNormal">Réel normal</option>
                    <option value="Franchise">Franchise en base</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelClass}>Date de clôture</label>
                <input value={editForm.date_cloture_exercice} onChange={(e) => setEditForm((f) => ({ ...f, date_cloture_exercice: e.target.value }))} placeholder="MM-DD" className={inputClass} />
              </div>
              {(() => {
                const currentEx = editCompany?.exercices?.find((ex) => ex.is_current) ?? editCompany?.exercices?.[0];
                if (!currentEx) return null;
                return (
                  <div>
                    <label className={labelClass}>Période d'exercice</label>
                    <div className={`${inputClass} bg-gray-50 text-gray-500 cursor-not-allowed`}>
                      {new Date(currentEx.debut).toLocaleDateString('fr-FR')} → {new Date(currentEx.fin).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                );
              })()}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setEditCompany(null)} className="flex-1 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Annuler</button>
              <button onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 py-2 text-sm font-medium text-white bg-amber-500 rounded-lg hover:bg-amber-600 disabled:opacity-50 transition-colors">{savingEdit ? 'Enregistrement...' : 'Enregistrer »'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
